<template>
  <layout :show-profile="true">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 pt-12">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-light text-gray-900">Werteräume</h2>
        <p class="text-sm text-gray-500">{{ calculations.total }} Berechnungen</p>
      </div>

      <horizontal-divider />

      <div v-if="topUsers.length" class="mb-6">
        <p class="mb-2 text-sm font-medium text-gray-700">Meiste Berechnungen in den letzten 30 Tagen</p>
        <div class="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-500">
          <span v-for="usage in topUsers" :key="usage.user_id">
            {{ usage.user ? usage.user.name : 'Gelöschter User' }}: {{ usage.calculation_count }}
          </span>
        </div>
      </div>

      <form class="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" @submit.prevent="applyFilters">
        <select v-model="form.user_id" class="rounded-md border-gray-300 text-sm focus:border-gray-400 focus:ring-gray-400">
          <option :value="null">Alle User</option>
          <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name }}</option>
        </select>

        <input v-model="form.word" type="search" placeholder="Wort suchen" class="rounded-md border-gray-300 text-sm focus:border-gray-400 focus:ring-gray-400" />
        <input v-model="form.date_from" type="date" class="rounded-md border-gray-300 text-sm focus:border-gray-400 focus:ring-gray-400" />
        <input v-model="form.date_to" type="date" class="rounded-md border-gray-300 text-sm focus:border-gray-400 focus:ring-gray-400" />

        <div class="flex gap-2">
          <button type="submit" class="rounded-md bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">Filtern</button>
          <button type="button" class="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50" @click="reset">Zurücksetzen</button>
        </div>
      </form>

      <div class="mt-6 overflow-x-auto shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
        <table class="min-w-full divide-y divide-gray-300">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900">Zeitpunkt</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900">User</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900">Wörter</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900">Ergebnis</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-for="calculation in calculations.data" :key="calculation.id">
              <td class="whitespace-nowrap px-4 py-4 text-sm text-gray-500">{{ calculation.created_at | formatDateTime }}</td>
              <td class="px-4 py-4 text-sm text-gray-700">
                <p>{{ calculation.user ? calculation.user.name : 'Gelöschter User' }}</p>
                <p v-if="calculation.partner_name" class="text-xs text-gray-400">{{ calculation.partner_name }}</p>
              </td>
              <td class="px-4 py-4 text-sm text-gray-700">
                <p class="font-medium">{{ calculation.word_count }} Wörter</p>
                <p class="mt-1 max-w-md text-xs leading-5 text-gray-500">{{ words(calculation) }}</p>
              </td>
              <td class="px-4 py-4 text-sm text-gray-700">
                <p :class="calculation.all_words_fit_in_value_zone ? 'text-markencode-green' : 'text-markencode-red'">
                  {{ calculation.all_words_fit_in_value_zone ? 'Alle Worte passen in Zone' : 'Nicht alle Worte passen in Zone' }}
                </p>
                <p v-if="calculation.status === 'completed'" class="mt-1 max-w-md text-xs leading-5 text-gray-500">{{ archetypes(calculation) }}</p>
                <p v-else class="mt-1 text-xs text-markencode-red">{{ calculation.error || 'Berechnung fehlgeschlagen' }}</p>
              </td>
            </tr>
            <tr v-if="!calculations.data.length">
              <td colspan="4" class="px-4 py-8 text-center text-sm text-gray-500">Keine Berechnungen gefunden.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <nav v-if="calculations.last_page > 1" class="mt-6 flex flex-wrap gap-1" aria-label="Seitennavigation">
        <template v-for="link in calculations.links">
          <Link v-if="link.url" :key="link.label" :href="link.url" v-html="link.label" class="rounded border px-3 py-2 text-sm" :class="link.active ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-50'" />
          <span v-else :key="link.label" v-html="link.label" class="rounded border border-gray-200 px-3 py-2 text-sm text-gray-300" />
        </template>
      </nav>
    </div>
  </layout>
</template>

<script>
import Layout from '@/Pages/ControlPanel/Shared/Layout';
import HorizontalDivider from '@/Shared/HorizontalDivider';
import { pickBy } from 'lodash';

export default {
  metaInfo: { title: 'Werteräume' },

  components: {
    Layout,
    HorizontalDivider,
  },

  props: ['calculations', 'filters', 'users', 'topUsers'],

  data() {
    return {
      form: {
        user_id: this.filters.user_id || null,
        word: this.filters.word || null,
        date_from: this.filters.date_from || null,
        date_to: this.filters.date_to || null,
      },
    };
  },

  methods: {
    applyFilters() {
      this.$inertia.get(this.route('admin.value-space-calculations.index'), pickBy(this.form));
    },

    reset() {
      this.form = {
        user_id: null,
        word: null,
        date_from: null,
        date_to: null,
      };
      this.applyFilters();
    },

    words(calculation) {
      return (calculation.selected_words || []).map((word) => word.word).join(', ');
    },

    archetypes(calculation) {
      return (calculation.result_archetypes || [])
        .map((archetype) => {
          const name = archetype.extended_name || archetype.name;
          const ranking = archetype.ranking;

          return ranking === null || ranking === undefined
            ? name
            : `${name} (${Number(ranking).toLocaleString('de-DE')})`;
        })
        .join(', ');
    },
  },
};
</script>
